import React, { ReactNode } from "react";

import { faSort } from "@fortawesome/pro-duotone-svg-icons";
import { faSort as faSortSolid, faSparkles } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

// Components
import { ButtonIcon, Text, Tooltip } from "@performyard/py-components";

import { getColor } from "../../../../utils/getColor";
// utils
import { Hex, chooseBestContrastingColor } from "../../../../utils/getContrast";

// css
import css from "./HeatmapTable.module.scss";

// Helper function to get the style for a cell based on the column and row id
const getCellStyle = (column: IHeatmapColumn, rowId: string, backgroundColor: string): React.CSSProperties => {
	return rowId in column.data && column.data[rowId] !== null
		? {
				backgroundColor,
				color: chooseBestContrastingColor({
					background: (backgroundColor.length ? backgroundColor : "#FFF") as Hex,
					standard: 1.9,
				}) as string,
			}
		: {};
};

// Helper function to determine cell CSS classes
const getCellClassName = (column: IHeatmapColumn, rowId: string, nullCellClass: string): string => {
	const isAlignLeft = column.alignLeft && typeof column.data[rowId] === "string";

	const hasNoCellRendererData =
		!column.cellRendererData || !(rowId in column.cellRendererData) || column.cellRendererData[rowId] === null;
	const hasNoData = !(rowId in column.data) || column.data[rowId] === null;
	const isNullCell = hasNoCellRendererData && hasNoData;

	const isRespondentCountsColumn = column.columnId === "respondent_counts";

	return [
		css.cellContent,
		isAlignLeft && css.alignLeft,
		isNullCell && (column.nullCellClass || nullCellClass),
		isRespondentCountsColumn && css.respondentCountsCell,
	]
		.filter(Boolean)
		.join(" ");
};

// Helper function to render cell content
const renderCellContent = (
	column: IHeatmapColumn,
	rowId: string,
	onOptionSelect?: (option: string, rowId: string) => void
) => {
	if (column.cellRenderer && column.cellRendererData?.[rowId]) {
		return column.cellRenderer(column.cellRendererData[rowId]);
	} else {
		if (column.data[rowId] === "options") {
			return (
				<div className={css.optionsCell}>
					<ButtonIcon
						onClick={() => onOptionSelect?.("ai_analysis", rowId)}
						icon={faSparkles}
						color="primary"
						shade={5}
						size={16}
						dangerouslySetClassName="bg-transparent hover:bg-transparent active:bg-transparent"
						data-testid="cohort-ai-analysis-button"
					/>
				</div>
			);
		}

		if (typeof column.data[rowId] === "string" && column.maxTextLength) {
			return (
				<div className="position-relative ml-4 inline-block">
					<Text size="caption" weight="regular">
						<div className="w-full py-line-clamp-1">{column.data[rowId] as string}</div>
					</Text>
				</div>
			);
		}

		if (column.nullTooltipText && !column.data[rowId]) {
			return (
				<div className="h-full w-full">
					<Tooltip
						content={
							<div className="w-[135px]">
								<Text size="caption" weight="regular">
									{column.nullTooltipText}
								</Text>
							</div>
						}
						asChild
					>
						<div className="grid h-full w-full place-items-center"></div>
					</Tooltip>
				</div>
			);
		}

		return (
			<Text size="paragraph-2" weight="semi-bold">
				{column.data[rowId]}
			</Text>
		);
	}
};

export interface IHeatmapColumn {
	/** The name for the column, if any */
	label?: string | ReactNode;
	/** give the column an id to refer to for sorting */
	columnId: string;
	/**
	 * The data for the column, with the key being the row number
	 * and the value being the data to display.
	 * This is a map to allow for easier sorting.
	 */
	data: { [key: string]: number | string };
	/**
	 * custom cell renderer
	 */
	cellRenderer?: (value: { [key: string]: number }) => ReactNode;
	/**
	 * the data for the cell renderer
	 */
	cellRendererData?: { [key: string]: { [key: string]: number } };
	/** should the value display to the left */
	alignLeft?: boolean;
	/** max text length can be set for strings */
	maxTextLength?: number;
	/** a fixed width for the column if it has one */
	minWidth?: string;
	/** class to use for a null cell, overrides table definition for the column */
	nullCellClass?: string;
	/**
	 * text to display in a tooltip for a null cell
	 */
	nullTooltipText?: string | null;
	/** function to use to decide what color
	 * to use based on the value, for this column.
	 * overrides the defaultSelectBackgroundColor prop
	 */
	selectBackgroundColor?: (value: number | string) => string;
}

export interface IColumnSort {
	columnId: string;
	direction: "asc" | "desc";
}

interface HeatmapTableProps {
	/** Each column is an object with the properties and data for that column on the table */
	columns: IHeatmapColumn[];
	/** sorted rowIds for the table rows, need to match each column's `data` keys */
	rowIds: string[];
	/** sorted column info for labelling */
	sortedColumn: IColumnSort;
	/** Optional title for the table, goes inside <caption> element */
	title?: ReactNode;
	/** Callback for when the sorting changes */
	onSortingChanged: (newSorting: string[], sortedColumn: IColumnSort) => void;
	/** function to use to decide what color a cell should be based on it's value */
	defaultSelectBackgroundColor?: (value: number | string) => string;
	/** function to use to format a string value */
	formatStringValue?: (value: string) => string;
	/** class to use for a null cell */
	nullCellClass?: string;
	/** if the top row is frozen and omitted from sorting */
	frozenTopRow?: boolean;
	onOptionSelect?: (option: string, rowId: string) => void;
}

const HeatmapTable = ({
	columns,
	title,
	rowIds,
	sortedColumn,
	onSortingChanged,
	defaultSelectBackgroundColor = () => "",
	nullCellClass = css.nullCell,
	frozenTopRow = false,
	onOptionSelect,
}: HeatmapTableProps) => {
	const sortByColumn = (columnId: string, sortDirection: "asc" | "desc") => {
		// find the column that was clicked
		const column = columns.find((column) => column.columnId === columnId);
		if (!column) {
			return;
		}

		const startRow = frozenTopRow ? 1 : 0;

		// to sort all the rows based on the data in the column that was clicked
		const sorted = [
			...Object.entries(column.data)
				.splice(startRow, rowIds.length)
				.sort((a, b) =>
					(typeof a[1] === "string" ? a[1].trim().toLowerCase() : a[1]) >=
					(typeof b[1] === "string" ? b[1].trim().toLowerCase() : b[1])
						? 1
						: -1
				)
				.map((entry) => entry[0]),
		];

		// add any missing rows to the end of the sorted list
		const missingRows = rowIds.filter((rowId) => !sorted.includes(rowId) && (!frozenTopRow || rowId !== rowIds[0]));
		sorted.push(...missingRows);

		if (sortDirection === "desc") {
			sorted.reverse();
		}

		const returnSorted = [...(frozenTopRow ? [rowIds[0]] : []), ...sorted];

		onSortingChanged(returnSorted, { columnId, direction: sortDirection });
	};

	const getColumnDirection = (columnId: string): "asc" | "desc" => {
		const columnAlreadySortedAsc = sortedColumn?.columnId === columnId && sortedColumn?.direction === "asc";
		return columnAlreadySortedAsc ? "desc" : "asc";
	};

	const onClickColumnHeader = (columnId: string) => {
		// first determine if there's data in this column
		const column = columns.find((column) => column.columnId === columnId);
		const columnDataLength = column ? Object.keys(column.data).filter((col) => column.data[col]).length : 0;
		// doesn't invoke sortBy function if no row contains data
		if (columnDataLength) {
			const direction = getColumnDirection(columnId);
			sortByColumn(columnId, direction);
		}
	};

	return (
		<table className={css.table} role="presentation">
			{title ? <caption className={css.caption}>{title}</caption> : null}
			<thead>
				<tr className={css.headerRow}>
					{columns.map((column) => (
						<td
							key={`header-${column.columnId}`}
							className={`${css.header} ${column.columnId === "options" ? css.optionsColumn : ""}`}
							style={{
								minWidth: column.minWidth,
							}}
						>
							<div
								className={`${css.headerCell} ${column.alignLeft ? css.alignLeft : ""}`}
								onClick={() => onClickColumnHeader(column.columnId)}
							>
								{typeof column.label === "string" ? (
									<Text size="caption" weight="semi-bold">
										{column.label}
									</Text>
								) : (
									column.label
								)}
								{column.label && (
									<>
										{sortedColumn?.columnId === column.columnId ? (
											<FontAwesomeIcon
												size="xs"
												// 33 is 20% opacity
												color={`${getColor("neutral", 6)}`}
												icon={faSort}
												rotation={sortedColumn.direction === "asc" ? undefined : 180}
											/>
										) : (
											<FontAwesomeIcon
												size="xs"
												// 33 is 20% opacity
												color={`${getColor("neutral", 6)}33`}
												icon={faSortSolid}
											/>
										)}
									</>
								)}
							</div>
						</td>
					))}
				</tr>
			</thead>
			<tbody>
				{
					// map over row ids
					rowIds.map((rowId) => (
						<tr className={frozenTopRow ? css.frozen : undefined} key={`row-${rowId}`}>
							{columns.map((column) => {
								const backgroundColor: string = column.selectBackgroundColor
									? column.selectBackgroundColor(column.data[rowId])
									: defaultSelectBackgroundColor(column.data[rowId]);
								return (
									<td
										key={column.columnId + rowId}
										className={`${css.cell} ${column.columnId === "options" ? css.optionsColumn : ""}`}
										style={{
											minWidth: column.minWidth,
										}}
									>
										<div
											className={getCellClassName(column, rowId, nullCellClass)}
											style={getCellStyle(column, rowId, backgroundColor)}
										>
											{renderCellContent(column, rowId, onOptionSelect)}
										</div>
									</td>
								);
							})}
						</tr>
					))
				}
			</tbody>
		</table>
	);
};

export default HeatmapTable;
